import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Sun, Moon, Menu, X, ArrowRight, Twitter, Linkedin, Github, Mail } from 'lucide-react';
import BookDemoModal from '@/components/home/BookDemoModal';
import { authAPI } from '@/services/authAPI';

export function WhatsAppIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export function useWaLink() {
  const [waInfo, setWaInfo] = React.useState(null);
  React.useEffect(() => { authAPI.getWhatsAppInfo().then(setWaInfo).catch(() => { /* ignore */ }); }, []);
  const fallback = process.env.REACT_APP_WHATSAPP_NUMBER;
  return (waInfo?.enabled && waInfo.wa_link) || (fallback ? `https://wa.me/${fallback}?text=Hi%20Numuv` : null);
}

export function NumuvLogo({ className = '' }) {
  return (
    <span
      className={`slab items-center inline-flex text-[24px] md:text-[26px] font-extrabold tracking-[-0.01em] ${className}`}
      style={{ fontFamily: "'Roboto Slab', serif" }}
      data-testid="numuv-logo"
      data-brand-logo="1"
    >
      <span className="text-[color:var(--t1)]">nu</span>
      <span style={{ color: '#3C8F62' }}>mu</span>
      <span className="text-[color:var(--t1)]">v</span>
    </span>
  );
}

function ThemeButton() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  if (!mounted) return <div className="w-10 h-10" />;
  const isDark = theme === 'dark';
  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className="grid h-10 w-10 place-items-center rounded-full card thm t2 hover:text-[#6D5EF7] transition-colors"
      aria-label="Toggle theme"
      data-testid="marketing-theme-toggle"
    >
      {isDark ? <Sun className="w-[15px] h-[15px] text-[#F59E0B]" /> : <Moon className="w-[15px] h-[15px]" />}
    </button>
  );
}

const NAV = [
  { label: 'About Us', to: '/about' },
  { label: 'Platform', to: '/#platform' },
  { label: 'For Applicants', to: '/for-applicants' },
  { label: 'For Employers', to: '/for-employers' },
  { label: 'Meet Jay', to: '/meet-jay' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Blog', to: '/blog' },
];

const MOBILE_NAV = NAV;

export function MarketingNav({ onBookDemo }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const waLink = useWaLink();
  const goLogin = () => navigate('/login');
  return (
    <header className="sticky top-0 z-50 w-full glass border-b brd thm">
      <nav className="mx-auto flex h-[68px] md:h-[74px] max-w-[1240px] items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center" data-testid="marketing-nav-logo">
          <NumuvLogo />
        </Link>
        <div className="hidden lg:flex items-center gap-8 text-[14px] font-medium">
          {NAV.map((l) => (
            <Link key={l.label} to={l.to} className="navlink" data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer" aria-label="Start on WhatsApp"
              className="grid h-10 w-10 place-items-center rounded-full card thm text-[#25D366] hover:brightness-110 hover:-translate-y-0.5 transition"
              data-testid="nav-whatsapp-btn">
              <WhatsAppIcon className="w-[17px] h-[17px]" />
            </a>
          )}
          <ThemeButton />
          <button onClick={goLogin} className="px-4 py-2 text-[14px] font-medium navlink" data-testid="nav-signin-btn">Sign in</button>
          <button onClick={onBookDemo} className="aurora-btn rounded-full px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_10px_24px_rgba(109,94,247,.32)] hover:brightness-105 transition inline-flex items-center gap-1" data-testid="nav-book-demo-btn">
            Book a demo <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="flex md:hidden items-center gap-2">
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer" aria-label="Start on WhatsApp"
              className="grid h-10 w-10 place-items-center rounded-full card thm text-[#25D366]"
              data-testid="nav-whatsapp-btn-mobile">
              <WhatsAppIcon className="w-[17px] h-[17px]" />
            </a>
          )}
          <ThemeButton />
          <button onClick={() => setOpen(v => !v)} className="grid h-10 w-10 place-items-center rounded-full card thm t2" aria-label="Menu" data-testid="nav-mobile-toggle">
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="md:hidden border-t brd bg-[color:var(--surface)]">
          <div className="flex flex-col gap-1 px-5 py-4">
            {MOBILE_NAV.map((l) => (
              <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="py-2.5 text-[15px] navlink border-b brd" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link>
            ))}
            <button onClick={() => { setOpen(false); goLogin(); }} className="py-3 text-left text-[15px] navlink border-b brd" data-testid="mobile-nav-signin">Sign in</button>
            <button onClick={() => { setOpen(false); navigate('/login?mode=signup'); }} className="py-3 text-left text-[15px] navlink" data-testid="mobile-nav-signup">Sign up</button>
            <button onClick={() => { setOpen(false); onBookDemo(); }} className="mt-2 aurora-btn rounded-full px-5 py-3 text-[14.5px] font-semibold text-white" data-testid="mobile-nav-book-demo">
              Book a demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="relative z-10 border-t brd thm" style={{ background: 'var(--surface)' }} data-testid="marketing-footer">
      <div className="mx-auto grid max-w-[1240px] px-4 md:px-6 md:grid-cols-5 gap-8 py-12 md:py-16">
        <div className="md:col-span-2">
          <Link to="/" className="inline-flex"><NumuvLogo /></Link>
          <p className="mt-4 max-w-[320px] text-[13.5px] md:text-[14px] leading-[21px] md:leading-[22px] t3">
            The AI-native recruitment platform where applicants, staffing partners, and hiring teams converge.
          </p>
          <a href="mailto:hello@numuv.app" className="mt-3 inline-flex items-center gap-2 text-[13.5px] t2 hover:text-[#6D5EF7] transition" data-testid="footer-email">
            <Mail className="w-4 h-4" /> hello@numuv.app
          </a>
          <div className="mt-5 md:mt-6 flex items-center gap-3">
            {[
              { I: Twitter, href: 'https://twitter.com/numuv', label: 'Twitter' },
              { I: Linkedin, href: 'https://www.linkedin.com/company/numuv', label: 'LinkedIn' },
              { I: Github, href: 'https://github.com/numuv', label: 'GitHub' },
            ].map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="grid h-9 w-9 place-items-center rounded-full border brd t3 hover:text-[#6D5EF7] transition">
                <s.I className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
        <FooterColumn title="Platform" items={[
          { label: 'AI Matching', to: '/for-employers#ai-matching' },
          { label: 'Meet Jay', to: '/meet-jay' },
          { label: 'Screening', to: '/for-employers#screening' },
          { label: 'Reporting', to: '/for-employers#reporting' },
        ]} />
        <FooterColumn title="Solutions" items={[
          { label: 'For Applicants', to: '/for-applicants' },
          { label: 'For Staffing Orgs', to: '/for-employers#staffing' },
          { label: 'For Hiring Orgs', to: '/for-employers#hiring' },
          { label: 'Enterprise', to: '/pricing#enterprise' },
        ]} />
        <FooterColumn title="Company" items={[
          { label: 'About', to: '/about' },
          { label: 'Careers', to: '/careers' },
          { label: 'Blog', to: '/blog' },
          { label: 'Contact', to: '/contact-us' },
        ]} />
      </div>
      <div className="border-t brd">
        <div className="mx-auto flex flex-col sm:flex-row max-w-[1240px] items-center justify-between gap-3 px-4 md:px-6 py-5 md:py-6 text-[11.5px] md:text-[12.5px] t4">
          <span>© {new Date().getFullYear()} Numuv. All rights reserved.</span>
          <div className="flex items-center gap-5 md:gap-6">
            <Link to="/privacy" className="flink">Privacy</Link>
            <Link to="/terms" className="flink">Terms</Link>
            <Link to="/security" className="flink">Security</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, items }) {
  return (
    <div>
      <p className="dis text-[13px] md:text-[13.5px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{title}</p>
      <ul className="mt-3 md:mt-4 space-y-2 md:space-y-2.5 text-[13.5px] md:text-[14px]">
        {items.map((it) => (
          <li key={it.label}>
            {it.to.startsWith('http') ? (
              <a href={it.to} className="flink">{it.label}</a>
            ) : (
              <Link to={it.to} className="flink">{it.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Shared marketing wrapper: aurora bg + nav + footer + BookDemo modal, with SEO-friendly layout.
 */
export default function MarketingLayout({ children }) {
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <div className="numuv-aurora relative overflow-x-clip t1 antialiased thm" style={{ background: 'var(--bg)' }}>
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-70" style={{
          maskImage: 'radial-gradient(120% 90% at 50% 0%,#000 30%,transparent 75%)',
          WebkitMaskImage: 'radial-gradient(120% 90% at 50% 0%,#000 30%,transparent 75%)',
        }} />
        <div className="absolute -top-40 -left-32 w-[520px] md:w-[640px] h-[520px] md:h-[640px] rounded-full anim-drift"
          style={{ background: 'radial-gradient(circle,var(--glow1),transparent 62%)' }} />
        <div className="absolute top-[420px] -right-40 w-[560px] md:w-[700px] h-[560px] md:h-[700px] rounded-full anim-drift2"
          style={{ background: 'radial-gradient(circle,var(--glow2),transparent 62%)' }} />
      </div>
      <MarketingNav onBookDemo={() => setDemoOpen(true)} />
      <main className="relative z-10">
        {children({ onBookDemo: () => setDemoOpen(true) })}
      </main>
      <MarketingFooter />
      <BookDemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
